/**
 * Pure helpers for building the writing-module batch of answer submissions.
 *
 * Extracted from `useWritingModuleState` so we can unit-test the
 * "every-question-gets-saved" contract that prevents an entire skipped
 * section (e.g. fill-in-the-blank) from being silently dropped from grading.
 *
 * @module writingAnswerSubmission
 */

import type { EssayTaskData, FillBlankFormData } from './writingDataTransform';

/**
 * Per-question answer payload sent to `saveUserAnswersBatch`.
 *
 * The shape mirrors `UserAnswerSubmission` but with only the fields the
 * writing module sets — keeping it narrow keeps the helper pure and avoids
 * pulling in the full domain type for tests.
 */
export interface WritingAnswerSubmission {
  user_id: string;
  session_id: string;
  question_id: string;
  module: 'writing';
  section: number;
  answer_text: string;
}

interface BuildWritingAnswersInput {
  readonly userId: string;
  readonly sessionId: string;
  /**
   * Essay tasks presented to the learner (one per essay question across all
   * sections). Each task gets exactly one row in the batch, even when the
   * learner left it blank.
   */
  readonly essayTasks: readonly EssayTaskData[];
  /**
   * Essay content keyed by `essayTasks[i].questionId`. Missing entries are
   * treated as empty submissions.
   */
  readonly essayContentByTask: Readonly<Record<string, string>>;
  /**
   * Fill-in-the-blank tasks returned by `transformWritingModuleData`.
   *
   * The backend ships a *pool* of fill-in-the-blank tasks (alternates), but
   * `WritingInterface` only presents the **first** one (`fillBlankForms[0]`)
   * to the learner — the rest are never shown and never answerable. Only the
   * displayed form is persisted; the alternates are dropped so the results
   * page doesn't list 9 phantom "unanswered" rows for tasks the learner
   * never saw.
   */
  readonly fillBlankForms: readonly FillBlankFormData[];
  readonly fillBlankAnswers: Readonly<Record<string, string>>;
}

/**
 * Build the writing-module answer batch for a finish/submit action.
 *
 * Contract:
 * - Always emits one row per essay task that was *actually presented* to the
 *   learner (one per essay question across all sections) and one row for the
 *   displayed fill-in-the-blank form, even when those were left blank. Empty
 *   rows let the backend analyser deterministically grade a skipped task as
 *   0 instead of silently dropping it from the module average.
 * - Alternates in `fillBlankForms` beyond index 0 are intentionally ignored:
 *   the writing UI only displays `fillBlankForms[0]`, so saving the rest
 *   would create phantom "unanswered" rows for tasks the learner never saw
 *   (which is what caused the 10x section-1 inflation on the results page).
 * - Essay rows carry the trimmed essay text (empty string when skipped).
 * - The displayed fill-in-the-blank row carries a JSON array of blank values
 *   when at least one blank is filled. This preserves empty slots without
 *   using a visible delimiter that can conflict with learner input, e.g.
 *   `["Berlin", "", "Montag"]`; when every blank is empty the row carries
 *   an empty string so the analyser takes its empty-answer short-circuit.
 */
export function buildWritingAnswerSubmissions({
  userId,
  sessionId,
  essayTasks,
  essayContentByTask,
  fillBlankForms,
  fillBlankAnswers,
}: BuildWritingAnswersInput): WritingAnswerSubmission[] {
  const answers: WritingAnswerSubmission[] = [];

  for (const task of essayTasks) {
    const rawContent = essayContentByTask[task.questionId] ?? '';
    answers.push({
      user_id: userId,
      session_id: sessionId,
      question_id: task.questionId,
      module: 'writing',
      section: task.section,
      answer_text: rawContent.trim(),
    });
  }

  const displayedForm = fillBlankForms[0];
  if (displayedForm?.questionId) {
    const blankValues = displayedForm.blanks.map((blank) =>
      (fillBlankAnswers[blank.id] ?? '').trim(),
    );
    const hasAnyAnswer = blankValues.some((value) => value.length > 0);
    const serializedAnswer = hasAnyAnswer ? JSON.stringify(blankValues) : '';

    answers.push({
      user_id: userId,
      session_id: sessionId,
      question_id: displayedForm.questionId,
      module: 'writing',
      section: displayedForm.section,
      answer_text: serializedAnswer,
    });
  }

  return answers;
}
